// Offline mode engine (OFFLINE-PLAN.md): the fetch the Supabase client uses.
//   online   - requests go out as before; every successful read is kept in the cache (per login, per vessel)
//   offline  - reads come from the cache; the daily jobs (rules.js) are queued and answered as the page expects;
//              any other write fails with a clear "needs a connection" message
//   sync     - the queue goes out in order through the same REST API with the login's own token (RLS, triggers as
//              always); a record changed on the server since it was cached is NOT overwritten: "needs a look"
//   replay   - each queued change has its own id (x-hc-change-id; sql/security-1.sql keeps it, so the database applies it
//              once), a change already on the server is not sent again, and one tab syncs at a time (Web Locks)
// Offline also means "the network failed": a boat's Wi-Fi is often up with no internet behind it.
import { parseRequest, isAuthUrl, vesselOf, userOfToken, offlineJob, changeLabel, conflicts, stockConflict, restock, NEEDS_CONNECTION,
  recheck, alreadyApplied, isDuplicate, isRightsRefusal, RIGHTS_GONE, LOGIN_RENEW } from './rules.js'

const pad = n => String(n).padStart(12, '0')
const isNetworkError = e => e && (e.name === 'TypeError' || e.name === 'AbortError' || e.offline === true)
const jsonResponse = (obj, status = 200, extra = {}) => new Response(obj === undefined ? null : JSON.stringify(obj), { status, headers: { 'content-type': 'application/json', 'x-hc-offline': '1', ...extra } })
const pgError = (message, code, status) => jsonResponse({ message, code, details: null, hint: null }, status)
const KEEP_HEADERS = ['prefer', 'accept', 'content-type', 'content-profile', 'x-upsert', 'cache-control', 'x-hc-source', 'x-hc-comment']

export function createOffline({ realFetch, store, isOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine), now = () => Date.now(),
  getRights = () => null, getToken = async () => null, apikey = '', uuid = () => crypto.randomUUID(), maxCache = 600, timeoutMs = 15000, retryMs = 20000, onChange = () => {},
  baseUrl = '', lock = (name, fn) => (typeof navigator !== 'undefined' && navigator.locks && navigator.locks.request ? navigator.locks.request(name, fn) : fn()) }) {
  let downSince = null, lastTry = 0, remembered = 0                     // the network failed (Wi-Fi without internet) and has not worked since
  const offlineNow = () => !isOnline() || downSince != null
  // try the network: when it is up, or again every 20 s after it failed (the Wi-Fi says "connected" all along)
  const tryNetwork = () => isOnline() && (downSince == null || now() - lastTry >= retryMs)
  const changed = () => { try { onChange() } catch (e) {} }
  const markUp = () => { if (downSince != null) { downSince = null; changed() } }
  const markDown = () => { if (downSince == null) { downSince = now(); changed() } }

  async function timed(input, init) {
    if (!timeoutMs || typeof AbortController === 'undefined' || (init && init.signal)) return realFetch(input, init)
    const ac = new AbortController(), t = setTimeout(() => ac.abort(), timeoutMs)
    try { return await realFetch(input, { ...(init || {}), signal: ac.signal }) } finally { clearTimeout(t) }
  }
  const cacheKey = (uid, url, headers) => uid + '|' + (/pgrst\.object/.test(headers.get('accept') || '') ? 'one|' : 'many|') + url
  async function setMeta(uid, patch) { const m = (await store.get('meta', uid)) || {}; await store.put('meta', uid, { ...m, ...patch }) }

  // ---------- the cache ----------
  async function remember(uid, url, headers, req, res) {
    const text = await res.clone().text()
    if (text.length > 2e6) return
    let data = null
    try { data = text ? JSON.parse(text) : null } catch (e) { return }
    // one write per answer (its rows are looked up in it only when a change is queued - findRow)
    await store.put('cache', cacheKey(uid, url, headers), { url, table: req.table, vessel: vesselOf(req.query), body: text, range: res.headers.get('content-range'), at: now() })
    await setMeta(uid, { lastSync: now() })
    if (++remembered % 25 === 0) {                     // keep the cache to maxCache answers (checked now and then, not on every read)
      const all = await store.list('cache', uid + '|')
      if (all.length > maxCache) for (const e of all.sort((a, b) => a.value.at - b.value.at).slice(0, all.length - maxCache)) await store.del('cache', e.key)
    }
  }
  // the row a change is based on: the newest of a change still queued on this device (rows) and the newest cached answer
  // that holds it. A rows entry whose change has synced, or that is older than a later read, is stale (it would hide
  // someone else's save: no "needs a look") and is dropped.
  const pendingOn = async (uid, table, id) => (await store.list('queue', uid + '|')).some(e => e.value.table === table && String(e.value.rowId) === String(id) && e.value.status !== 'failed')
  async function findRow(uid, table, id) {
    const key = uid + '|' + table + '|' + id
    const own = await store.get('rows', key)
    let best = null
    for (const e of await store.list('cache', uid + '|')) {
      if (e.value.table !== table || !e.value.body || (best && best.at >= e.value.at)) continue
      let data; try { data = JSON.parse(e.value.body) } catch (x) { continue }
      const hit = (Array.isArray(data) ? data : [data]).find(r => r && String(r.id) === String(id))
      if (hit) best = { at: e.value.at, row: hit }
    }
    if (own) {
      const at = own.at || 0, row = own.row || null
      if (row && (!best || at > best.at) && await pendingOn(uid, table, id)) return row
      await store.del('rows', key)
    }
    return best ? best.row : null
  }
  const dropRow = async (uid, table, id) => { if (table && id != null && !(await pendingOn(uid, table, id))) await store.del('rows', uid + '|' + table + '|' + id) }
  // a queued change shown at once: the row in every cached answer of that table (new rows where the list filter fits)
  async function patchCache(uid, table, row, isNew) {
    if (row && row.id != null) await store.put('rows', uid + '|' + table + '|' + row.id, { row, at: now() })
    for (const e of await store.list('cache', uid + '|')) {
      if (e.value.table !== table || !e.value.body) continue
      let data; try { data = JSON.parse(e.value.body) } catch (x) { continue }
      let hit = false
      if (Array.isArray(data)) {
        data = data.map(r => (r && r.id === row.id ? (hit = true, { ...r, ...row }) : r))
        if (!hit && isNew && fits(e.value.url, row)) { data.push(row); hit = true }
      } else if (data && data.id === row.id) { data = { ...data, ...row }; hit = true }
      if (hit) await store.put('cache', e.key, { ...e.value, body: JSON.stringify(data) })
    }
  }
  function fits(url, row) {
    let q; try { q = new URL(url).searchParams } catch (e) { return false }
    for (const [k, v] of q) {
      if (['select', 'order', 'limit', 'offset', 'columns', 'on_conflict'].includes(k)) continue
      let m
      if ((m = v.match(/^eq\.(.*)$/))) { if (String(row[k]) !== decodeURIComponent(m[1])) return false }
      else if ((m = v.match(/^in\.\((.*)\)$/))) { if (!m[1].split(',').map(x => x.replace(/^"|"$/g, '')).includes(String(row[k]))) return false }
      else if (v === 'is.null') { if (row[k] != null) return false }
      else return false                                    // a filter we cannot check: the list reloads when online
    }
    return true
  }

  // ---------- the queue ----------
  async function enqueue(uid, req, url, init, headers, body, upload) {
    const rights = getRights() || {}
    const cachedRow = req.kind === 'rpc' ? await findRow(uid, 'inventory', body && body.p_item) : req.id ? await findRow(uid, req.table, req.id) : null
    const job = offlineJob(req, body, rights, cachedRow)
    if (!job.ok) return pgError(job.why, job.module ? 'OFFLINE_RIGHTS' : 'OFFLINE', job.module ? 403 : 503)
    let u = url, b = body
    // a new record gets its id here, so sending it twice can never make two
    if (req.kind === 'write' && req.method === 'POST') {
      b = (Array.isArray(body) ? body : [body]).map(r => (r && r.id ? r : { ...r, id: uuid() }))
      try { const x = new URL(url); const cols = x.searchParams.get('columns'); if (cols && !/"id"/.test(cols)) { x.searchParams.set('columns', cols + ',"id"'); u = x.toString() } } catch (e) {}
      b = Array.isArray(body) ? b : b[0]
    }
    const base = req.kind === 'rpc' ? { quantity: cachedRow ? cachedRow.quantity : null }
      : req.method === 'PATCH' && cachedRow ? Object.fromEntries(Object.keys(body || {}).filter(k => k in cachedRow).map(k => [k, cachedRow[k]])) : null
    const keep = {}; for (const h of KEEP_HEADERS) if (headers.get(h)) keep[h] = headers.get(h)
    const seq = ((await store.list('queue', uid + '|')).reduce((m, e) => Math.max(m, e.value.seq), 0)) + 1
    const item = { seq, uid, vessel: vesselOf(req.query, b) || rights.vesselId || null, at: now(), method: req.method || init.method || 'POST', url: u, headers: keep,
      body: upload ? null : JSON.stringify(b), upload: upload || null, kind: req.kind, table: req.table || (req.kind === 'rpc' ? 'inventory' : null), fn: req.fn || null,
      rowId: req.kind === 'rpc' ? body.p_item : req.id || (b && !Array.isArray(b) ? b.id : null), label: changeLabel(req, b, cachedRow), base, status: 'waiting', changeId: uuid() }
    await store.put('queue', uid + '|' + pad(seq), item)
    changed()
    // the answer the page expects, and the change shown in the cached lists
    const wantRep = /return=representation/.test(headers.get('prefer') || ''), one = /pgrst\.object/.test(headers.get('accept') || '')
    const rep = rows => (!wantRep ? new Response(null, { status: req.method === 'POST' ? 201 : 204, headers: { 'x-hc-offline': '1' } }) : jsonResponse(one ? rows[0] : rows, req.method === 'POST' ? 201 : 200))
    if (req.kind === 'upload') return jsonResponse({ Key: req.bucket + '/' + req.path, Id: 'offline-' + seq }, 200)
    if (req.kind === 'rpc') {
      const row = { ...(cachedRow || { id: body.p_item }), quantity: body.p_quantity }
      await patchCache(uid, 'inventory', row, false)
      return jsonResponse({ item: row.part_name || null, before: cachedRow ? cachedRow.quantity : null, after: body.p_quantity, reason: body.p_reason, offline: true })
    }
    if (req.method === 'PATCH') {
      const row = { ...(cachedRow || { id: req.id }), ...body }
      await patchCache(uid, req.table, row, false)
      return rep([row])
    }
    const rows = (Array.isArray(b) ? b : [b]).map(r => ({ created_at: new Date(now()).toISOString(), ...r }))
    for (const r of rows) await patchCache(uid, req.table, r, true)
    return rep(rows)
  }

  // ---------- the fetch the Supabase client uses ----------
  async function offlineFetch(input, init = {}) {
    const url = typeof input === 'string' ? input : input.url
    // login, token refresh, sign-out (/auth/v1/): straight to the network - never cached, never queued, never answered offline
    if (isAuthUrl(url)) return realFetch(input, init)
    const headers = new Headers(init.headers || (typeof input === 'object' && input.headers) || {})
    const req = parseRequest(url, init.method || (typeof input === 'object' && input.method) || 'GET')
    const uid = userOfToken(headers.get('authorization'))
    if (req.kind === 'other' || !uid) return realFetch(input, init)      // log in, token refresh, functions: as always
    if (req.kind === 'read') {
      if (tryNetwork() || (req.method === 'HEAD' && isOnline())) {
        lastTry = now()
        try {
          const res = await timed(input, init)
          markUp()
          if (res.ok && req.method === 'GET') await remember(uid, url, headers, req, res).catch(() => {})
          return res
        } catch (e) { if (!isNetworkError(e)) throw e; markDown() }
      }
      const c = await store.get('cache', cacheKey(uid, url, headers))
      if (c) return new Response(c.body, { status: 200, headers: { 'content-type': 'application/json', 'x-hc-offline': '1', ...(c.range ? { 'content-range': c.range } : {}) } })
      return pgError('Offline - this was not loaded before on this device. It will load when you are back online.', 'OFFLINE', 503)
    }
    // a write / rpc / upload
    let body = null, upload = null
    if (req.kind === 'upload') {
      const fd = init.body
      if (typeof FormData !== 'undefined' && fd instanceof FormData) { const f = {}; let blob = null, name = 'file'; for (const [k, v] of fd.entries()) { if (typeof v === 'string') f[k] = v; else { blob = v; name = k } } upload = { blob, field: name, fields: f } }
      else upload = { blob: fd, field: null, fields: {} }
    } else { try { body = init.body ? JSON.parse(init.body) : null } catch (e) { body = null } }
    if (tryNetwork()) {
      lastTry = now()
      try { const res = await timed(input, init); markUp(); return res } catch (e) { if (!isNetworkError(e)) throw e; markDown() }
    }
    return enqueue(uid, req, url, init, headers, body, upload)
  }

  // ---------- sync ----------
  // the answer of a refused change -> the message shown under "not saved"
  async function refusal(res) {
    let j = null
    try { j = await res.json() } catch (x) {}
    return { j, msg: isRightsRefusal(res.status, j) ? RIGHTS_GONE : (j && (j.message || j.error || j.msg)) || 'HTTP ' + res.status }
  }
  const fail = async (key, it, error) => { await store.put('queue', key, { ...it, status: 'failed', error }); changed() }
  const done = async (uid, key, it) => { await store.del('queue', key); await dropRow(uid, it.table, it.rowId); await setMeta(uid, { lastSync: now() }); changed() }
  let syncing = null
  async function sync(uid) {
    if (!uid || !isOnline()) return { sent: 0 }
    if (syncing) return syncing
    // one tab at a time (two tabs share the queue): the other one finds it sent
    syncing = Promise.resolve(lock('hc-offline-sync:' + uid, () => syncLocked(uid)))
    try { return await syncing } finally { syncing = null }
  }
  async function syncLocked(uid) {
    const waiting = (await store.list('queue', uid + '|')).filter(e => e.value.status === 'waiting')
    const token = await getToken()
    if (!token || userOfToken('Bearer ' + token) !== uid) {   // only the login that made the changes sends them
      // no token for that login (it could not be renewed - deactivated?): said on the bar, the queue kept as it is
      if (waiting.length && !token) { await setMeta(uid, { blocked: LOGIN_RENEW }); changed() }
      return { sent: 0 }
    }
    const auth = { apikey, Authorization: 'Bearer ' + token }
    if (((await store.get('meta', uid)) || {}).blocked) { await setMeta(uid, { blocked: null }); changed() }
    let sent = 0
    for (const e of waiting) {
      const it = (await store.get('queue', e.key)) || null
      if (!it || it.status !== 'waiting') continue               // sent or dropped meanwhile
      try {
        // checked again now: only a daily job, to this server, and the login may still make it
        const rc = recheck(it, getRights(), baseUrl)
        if (!rc.ok) { await fail(e.key, it, rc.why); continue }
        const body = JSON.parse(it.body || '{}')
        // a change to an existing record: read it again; changed on the server since it was cached -> needs a look
        if ((it.method === 'PATCH' && it.base && Object.keys(it.base).length) || it.kind === 'rpc') {
          const restBase = it.url.split('/rest/v1/')[0] + '/rest/v1/'
          const r = await timed(restBase + it.table + '?select=*&id=eq.' + encodeURIComponent(it.rowId), { headers: auth })
          if (r.status === 401) { await setMeta(uid, { blocked: LOGIN_RENEW }); changed(); break }
          if (!r.ok) throw Object.assign(new Error('read ' + r.status), { status: r.status })
          const [server] = await r.json()
          if (!server) { await fail(e.key, it, 'Not saved: the record is gone, or this login can no longer see it (rights changed or login deactivated) - nothing was written.'); continue }
          // sent before (its answer was lost) or by another tab: already there -> synced, not sent a second time
          if (alreadyApplied(it, server, body)) { await done(uid, e.key, it); sent++; continue }
          const cf = it.kind === 'rpc'
            ? (stockConflict(it.base.quantity, server.quantity) ? [{ field: 'quantity', base: it.base.quantity, server: server.quantity, mine: body.p_quantity }] : [])
            : conflicts(it.base, server, body)
          if (cf.length) { await store.put('queue', e.key, { ...it, status: 'conflict', conflict: cf, serverRow: server }); changed(); continue }
        }
        let payload = it.body
        if (it.upload) {
          if (it.upload.field) { payload = new FormData(); for (const [k, v] of Object.entries(it.upload.fields || {})) payload.append(k, v); payload.append(it.upload.field, it.upload.blob) }
          else payload = it.upload.blob
        }
        const headers = { ...it.headers, ...auth, 'x-hc-offline-at': new Date(it.at).toISOString(), ...(it.changeId ? { 'x-hc-change-id': it.changeId } : {}) }
        // a change of an existing record answers with the rows it changed: none = the row rules refused it (no error from the server)
        if (it.method === 'PATCH' && it.kind === 'write') headers.prefer = [String(headers.prefer || '').split(',').map(x => x.trim()).filter(x => x && !/^return=/.test(x)).join(','), 'return=representation'].filter(Boolean).join(',')
        if (it.upload && it.upload.field) delete headers['content-type']
        const res = await timed(it.url, { method: it.method, headers, body: payload })
        markUp()
        if (res.ok) {
          if (it.method === 'PATCH' && it.kind === 'write') {
            let rows = null
            try { rows = await res.clone().json() } catch (x) {}
            if (Array.isArray(rows) && rows.length === 0) { await fail(e.key, it, RIGHTS_GONE); continue }
          }
          await done(uid, e.key, it); sent++; continue
        }
        if (res.status === 401) { await setMeta(uid, { blocked: LOGIN_RENEW }); changed(); break }   // token gone: kept, sent after the next login
        const { j, msg } = await refusal(res)
        if (isDuplicate(res.status, j, it)) { await done(uid, e.key, it); sent++; continue }          // it already arrived
        await fail(e.key, it, msg)
      } catch (err) {
        if (isNetworkError(err)) { markDown(); break }                               // still offline: try again later
        await fail(e.key, it, err.message)
      }
    }
    return { sent }
  }

  // ---------- "needs a look": the user chooses ----------
  async function resolve(uid, seq, choice) {
    const key = uid + '|' + pad(seq), it = await store.get('queue', key)
    if (!it || it.status !== 'conflict') return false
    if (choice === 'server') {
      await store.del('queue', key)
      if (it.serverRow && it.table) { await patchCache(uid, it.table, it.serverRow, false); await store.del('rows', uid + '|' + it.table + '|' + it.rowId) }   // show the server's value again
    } else {
      let body = it.body
      if (it.kind === 'rpc') { const b = JSON.parse(it.body); b.p_quantity = restock(it.base.quantity, b.p_quantity, it.serverRow.quantity); body = JSON.stringify(b) }
      const base = it.kind === 'rpc' ? { quantity: it.serverRow.quantity } : Object.fromEntries(Object.keys(it.base || {}).map(k => [k, it.serverRow[k]]))
      await store.put('queue', key, { ...it, body, base, status: 'waiting', conflict: null, serverRow: null })
    }
    changed()
    return true
  }
  async function dismiss(uid, seq) { const it = await store.get('queue', uid + '|' + pad(seq)); await store.del('queue', uid + '|' + pad(seq)); if (it) await dropRow(uid, it.table, it.rowId); changed() }
  async function status(uid) {
    const q = uid ? (await store.list('queue', uid + '|')).map(e => e.value) : []
    const m = uid ? (await store.get('meta', uid)) || {} : {}
    return { offline: offlineNow(), lastSync: m.lastSync || null, blocked: m.blocked || null, waiting: q.filter(i => i.status === 'waiting' || i.status === 'syncing'), conflicts: q.filter(i => i.status === 'conflict'), failed: q.filter(i => i.status === 'failed') }
  }
  // logging out: everything of this login (cache, rows, queue, meta)
  async function wipe(uid) { if (!uid) return; for (const s of ['cache', 'rows', 'queue']) await store.clear(s, uid + '|'); await store.del('meta', uid); changed() }
  async function wipeAll() { for (const s of ['cache', 'rows', 'queue', 'meta']) await store.clear(s); changed() }
  // the login's rights changed: its cached data may hold what it can no longer read -> drop the cache (the queue stays,
  // the database checks every change again when it is sent)
  async function checkRights(uid, hash) {
    const m = (await store.get('meta', uid)) || {}
    if (m.permHash && m.permHash !== hash) { await store.clear('cache', uid + '|'); await store.clear('rows', uid + '|') }
    await setMeta(uid, { permHash: hash })
  }
  return { fetch: offlineFetch, sync, resolve, dismiss, status, wipe, wipeAll, checkRights, isOffline: offlineNow, markUp, markDown }
}
export { NEEDS_CONNECTION }
